import { useCallback } from 'react';
import { sound } from '@pixi/sound';
import useCanvasStore from '../stores/canvasStore';
import {
  getGlobalCreateVideoSprite,
  getGlobalCreateImageSprite,
  getGlobalCreateAudioSprite,
  getGlobalCreateGifSprite,
} from '../stores/globalStore';

/**
 * useUpdateAiSpriteMedia
 *
 * Replaces the visual media on an AI sprite while preserving `type: 'ai'`
 * and the `aiPayload` in componentData. This allows the user to select
 * different generated results (or switch between image/video/audio) without
 * losing the AI properties panel or the ability to regenerate.
 *
 * Unlike `replaceElementMedia` (which changes the element type to the new
 * media type), this hook keeps the element as an AI sprite so the chat /
 * history UI stays attached.
 */
export const useUpdateAiSpriteMedia = () => {
  const viewport = useCanvasStore((s) => s.viewport);
  const canvasElements = useCanvasStore((s) => s.canvasElements);
  const updateElement = useCanvasStore((s) => s.updateElement);
  const selectElement = useCanvasStore((s) => s.selectElement);
  const isPlaying = useCanvasStore((s) => s.isPlaying);
  const stopPlaybackLoop = useCanvasStore((s) => s.stopPlaybackLoop);

  const updateAiSpriteMedia = useCallback(
    async (elementId, newUrl, newMediaType, newName, thumbnailUrl, newAiPayload) => {
      if (!viewport) {
        console.warn('Viewport not available for AI sprite media update');
        return;
      }

      const existingElement = canvasElements.find((el) => el.id === elementId);
      if (!existingElement) {
        console.warn(`Element with ID ${elementId} not found for AI media update`);
        return;
      }

      // Resolve sprite creators from the global store
      const createImageSprite = getGlobalCreateImageSprite();
      const createVideoSprite = getGlobalCreateVideoSprite();
      const createAudioSprite = getGlobalCreateAudioSprite();
      const createGifSprite = getGlobalCreateGifSprite();

      try {
        // Preserve the existing aiPayload so the sprite stays as type:'ai'
        const oldComponentData = existingElement.componentData || {};
        const isPlayableType = newMediaType === 'video' || newMediaType === 'audio';
        const newComponentData = {
          ...oldComponentData,
          url: newUrl,
          preview: isPlayableType ? (thumbnailUrl || null) : newUrl,
          thumbnail: isPlayableType ? (thumbnailUrl || null) : (thumbnailUrl || newUrl),
          // Keep aiPayload intact — this is what makes it an AI sprite
          // If a new aiPayload is provided (from selecting a different generation),
          // update it so the payload tags reflect the selected generation's info.
          aiPayload: newAiPayload || oldComponentData.aiPayload,
          // Track the current media type for rendering, but element type stays 'ai'
          aiMediaType: newMediaType,
        };
        if (newName) {
          newComponentData.name = newName;
        }

        // ---- Step 1: Stop playback ----
        if (isPlaying) {
          stopPlaybackLoop();
        }

        // ---- Step 2: Capture old sprite transforms ----
        const oldSprite = existingElement.pixiSprite;
        let savedAlpha = 1, savedWidth = 0, savedHeight = 0, savedRotation = 0, savedZIndex = 0;
        if (oldSprite && !oldSprite.destroyed && oldSprite.scale !== null) {
          savedAlpha = oldSprite.alpha ?? 1;
          savedWidth = oldSprite.width || 0;
          savedHeight = oldSprite.height || 0;
          savedRotation = oldSprite.rotation ?? 0;
          savedZIndex = oldSprite.zIndex ?? 0;
        }

        // ---- Step 2.5: Cleanup old audio/video ----
        if (oldSprite && !oldSprite.destroyed) {
          if (oldSprite.soundId) {
            try {
              if (sound.exists(oldSprite.soundId)) {
                sound.stop(oldSprite.soundId);
                sound.remove(oldSprite.soundId);
              }
              if (oldSprite.pixiSound) {
                oldSprite.pixiSound.stop();
              }
            } catch (e) {
              console.warn('Error cleaning up old audio:', e);
            }
            if (oldSprite.audioElement) {
              try {
                if (oldSprite.audioElement.source) {
                  oldSprite.audioElement.source.stop();
                } else if (oldSprite.audioElement.pause) {
                  oldSprite.audioElement.pause();
                }
              } catch (e) { /* ignore */ }
            }
            oldSprite.soundId = null;
            oldSprite.pixiSound = null;
            oldSprite.audioElement = null;
            oldSprite._cleanupAudio = () => {};
          }
          if (oldSprite.videoElement) {
            oldSprite.videoElement.pause();
            oldSprite.videoElement.removeAttribute('src');
            oldSprite.videoElement.load();
          }
          if (oldSprite._cleanupVideo) {
            oldSprite._cleanupVideo();
          }
          if (oldSprite._cleanupAnimation) {
            oldSprite._cleanupAnimation();
          }
        }

        // ---- Step 3: Create new sprite ----
        let newSprite = null;
        if (newMediaType === 'gif') {
          newSprite = await createGifSprite(newComponentData, elementId, existingElement.x, existingElement.y);
        } else if (newMediaType === 'image') {
          newSprite = await createImageSprite(newComponentData, elementId, existingElement.x, existingElement.y);
        } else if (newMediaType === 'audio') {
          newSprite = await createAudioSprite(newComponentData, elementId, existingElement.x, existingElement.y);
        } else {
          newSprite = await createVideoSprite(newComponentData, elementId, existingElement.x, existingElement.y, null);
        }

        if (!newSprite) {
          console.error('Failed to create replacement sprite for AI media update');
          return;
        }

        // ---- Step 4: Scale new sprite to match old size ----
        newSprite.alpha = savedAlpha;
        newSprite.rotation = savedRotation;
        newSprite.zIndex = savedZIndex;

        if (savedWidth > 0 && savedHeight > 0) {
          newSprite.scale.set(1, 1);
          const nativeW = newSprite.width || 1;
          const nativeH = newSprite.height || 1;
          const newScaleX = savedWidth / nativeW;
          const newScaleY = savedHeight / nativeH;
          newSprite.scale.set(newScaleX, newScaleY);
        }

        if (newSprite.interactiveArea) {
          newSprite.interactiveArea.alpha = savedAlpha;
          newSprite.interactiveArea.scale.set(newSprite.scale.x, newSprite.scale.y);
          newSprite.interactiveArea.rotation = savedRotation;
          newSprite.interactiveArea.zIndex = savedZIndex;
        }

        // ---- Step 5: Swap containers ----
        const visualContainer = viewport.visualContainer || viewport;
        const interactiveContainer = viewport.interactiveContainer || viewport;

        if (oldSprite) {
          if (oldSprite.interactiveArea && oldSprite.interactiveArea.parent) {
            oldSprite.interactiveArea.parent.removeChild(oldSprite.interactiveArea);
          }
          if (oldSprite.parent) {
            oldSprite.parent.removeChild(oldSprite);
          }
        }

        visualContainer.addChild(newSprite);
        if (newSprite.interactiveArea) {
          interactiveContainer.addChild(newSprite.interactiveArea);
        }
        visualContainer.sortDirty = true;
        interactiveContainer.sortDirty = true;

        // ---- Step 6: Update store — KEEP type as 'ai' ----
        updateElement(elementId, {
          type: 'ai', // Preserve AI sprite type
          name: newName || existingElement.name,
          componentData: newComponentData,
          pixiSprite: newSprite,
          width: newSprite.width,
          height: newSprite.height,
          scaleX: newSprite.scale.x,
          scaleY: newSprite.scale.y,
          rotation: newSprite.rotation,
          alpha: newSprite.alpha,
        });

        // ---- Step 7: Destroy old sprite ----
        if (oldSprite && !oldSprite.destroyed) {
          oldSprite.destroy({ children: true });
        }

        // ---- Step 8: Update selection reference to the fresh element ----
        // This keeps the properties panel open without an unmount/remount cycle.
        const updatedEl = useCanvasStore.getState().canvasElements.find(el => el.id === elementId);
        if (updatedEl) {
          selectElement(updatedEl);
        }
      } catch (error) {
        console.error('Error updating AI sprite media:', error);
      }
    },
    [viewport, canvasElements, updateElement, selectElement, isPlaying, stopPlaybackLoop]
  );

  return updateAiSpriteMedia;
};
